<script setup>
import { onMounted, ref, watch } from 'vue'
import { RadioGroup, RadioGroupDescription, RadioGroupLabel, RadioGroupOption } from '@headlessui/vue'
import { storeToRefs } from 'pinia'
import { useInvestmentProcessDataState } from '../../../utilities/investment-process/useInvestmentProcess'
import { i18n } from '../../../localization'
import { getCampaignData } from '../../../utilities/investment-process/useCampaign'
const { contractType } = storeToRefs(useInvestmentProcessDataState())

const settings = [
    {
        name: i18n.global.t('investment_process.voucher_title'),
        description: i18n.global.t('investment_process.voucher_description', {
            interest_rate: getCampaignData().asset_data?.voucher_contract?.interest_rate,
        }),
        value: 'voucher',
    },
    {
        name: i18n.global.t('investment_process.cash_title'),
        description: i18n.global.t('investment_process.cash_description', {
            interest_rate: getCampaignData().asset_data?.contract?.interest_rate,
            currency:
                getCampaignData().asset_data.currency === '&euro;' ? 'EUR' : getCampaignData().asset_data.currency,
        }),
        value: 'cash',
    },
]

const selected = ref(settings[0])

onMounted(() => {
    contractType.value = selected.value.value
})

watch(selected, (value) => {
    contractType.value = value.value
})
</script>

<template>
    <p class="pb-3 text-base font-semibold text-gray-700">{{ $t('investment_process.which_contract_type') }}</p>

    <RadioGroup v-model="selected">
        <RadioGroupLabel class="sr-only">{{ $t('investment_process.which_contract_type') }}</RadioGroupLabel>
        <div class="-space-y-px rounded-md bg-white">
            <RadioGroupOption
                v-for="(setting, settingIdx) in settings"
                :key="setting.name"
                v-slot="{ checked, active }"
                as="template"
                :value="setting">
                <div
                    :class="[
                        settingIdx === 0 ? 'rounded-tl-md rounded-tr-md' : '',
                        settingIdx === settings.length - 1 ? 'rounded-bl-md rounded-br-md' : '',
                        checked ? 'z-10 border-primary-200 bg-primary-50' : 'border-gray-200',
                        'relative flex cursor-pointer border p-4 focus:outline-none',
                    ]">
                    <span
                        :class="[
                            checked ? 'border-transparent bg-primary-500' : 'border-gray-300 bg-white',
                            active ? 'ring-2 ring-primary-600 ring-offset-2' : '',
                            'mt-0.5 flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded-full border',
                        ]"
                        aria-hidden="true">
                        <span class="h-1.5 w-1.5 rounded-full bg-white" />
                    </span>
                    <span class="ml-3 flex flex-col">
                        <RadioGroupLabel
                            as="span"
                            :class="[checked ? 'text-primary-900' : 'text-gray-900', 'block text-sm font-semibold']"
                            >{{ setting.name }}</RadioGroupLabel
                        >
                        <RadioGroupDescription
                            as="span"
                            :class="[checked ? 'text-primary-700' : 'text-gray-500', 'block text-sm']"
                            >{{ setting.description }}</RadioGroupDescription
                        >
                    </span>
                </div>
            </RadioGroupOption>
        </div>
    </RadioGroup>
</template>

<style scoped></style>
